<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="new-practice-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="new-practice-form" @submit.prevent="onSubmit">
        <div class="new-practice-form-body">
          <v-toolbar flat height="76">
            <v-toolbar-title class="new-practice-toolbar-title"
              >{{ practiceUser.firstName }}
              {{ practiceUser.lastName }}</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-tabs class="new-practice-tabs" v-model="tab">
            <v-tabs-slider></v-tabs-slider>
            <v-tab>General</v-tab>
            <v-tab v-if="isAnyAdminRole">Assigned participants</v-tab>
          </v-tabs>
          <!-- practice -->
          <v-tabs-items class="new-practice-v-tabs-items" v-model="tab">
            <v-tab-item>
              <v-tabs class="new-practice-tabs" v-model="generalTab">
                <v-tabs-slider></v-tabs-slider>
                <v-tab>Personal data</v-tab>
              </v-tabs>
              <v-tabs-items
                class="new-practice-v-tabs-items"
                v-model="generalTab"
              >
                <v-tab-item>
                  <v-container class="new-practice-card-container pt-4">
                    <v-row>
                      <v-col sm="6">
                        <dialog-input
                          label="First name"
                          name="firstName"
                          :value="editedUser.firstName"
                          @input="onInput"
                          :readonly="!isAnyAdminRole || !isPracticeActive"
                        />
                      </v-col>
                      <v-col sm="6">
                        <dialog-input
                          label="Last name"
                          name="lastName"
                          :value="editedUser.lastName"
                          @input="onInput"
                          :readonly="!isAnyAdminRole || !isPracticeActive"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                  <v-container
                    class="new-practice-card-container pt-0"
                    v-if="practiceUser.lastActivity"
                  >
                    <label class="dialog-input-label">Last activity: </label
                    ><span>{{
                      $moment(practiceUser.lastActivity).format(
                        'MMMM DD, YYYY hh:mm A',
                      )
                    }}</span>
                  </v-container>
                  <v-container class="new-practice-card-container pt-0">
                    <dialog-input
                      label="Email address"
                      name="email"
                      :value="practiceUser.email"
                      prepend-icon="mdi-email-outline"
                      :readonly="
                        !isAnyAdminRole ||
                          !isEmailChangeAllowed ||
                          !isPracticeActive
                      "
                      @input="onInput"
                    />
                  </v-container>
                  <v-container class="new-practice-card-container pt-0">
                    <dialog-input
                      label="Phone"
                      name="phone"
                      :value="editedUser.phone"
                      prepend-icon="mdi-phone-outline"
                      @input="onInput"
                      :readonly="!isAnyAdminRole || !isPracticeActive"
                    />
                  </v-container>
                  <v-container class="new-practice-card-container py-0">
                    <v-row>
                      <v-col class="new-bh-user-form-col-left" sm="6">
                        <dialog-select
                          :disabled="!isAnyAdminRole || !isPracticeActive"
                          label="Role"
                          name="roleId"
                          :items="roleIdOptions"
                          @input="onInput"
                          :value="editedUser.roleId"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                  <v-container
                    class="new-practice-card-container py-0 pt-2"
                    v-if="isAdvisorRoleSelected"
                  >
                    <bh-select
                      :disabled="!isAnyAdminRole || !isPracticeActive"
                      class="mb-3"
                      label="AI Lens"
                      v-model="editedUser.aiLens"
                      :items="AI_LENS_OPTIONS"
                      item-text="text"
                      item-value="value"
                      @input="onInput({ name: 'aiLens', value: $event })"
                    />
                  </v-container>
                  <v-container class="new-practice-card-container pb-0">
                    <bh-select
                      class="mb-3"
                      :disabled="!isAnyAdminRole || !isPracticeActive"
                      :label="tenantTexts.practiceUserDialog.surveyFlag"
                      v-model="practiceUser.surveyAvailable"
                      :items="computedFeatureFlagsSurvey"
                      item-text="text"
                      item-value="value"
                      @input="changeFeatureFlag('surveyAvailable', $event)"
                    />
                  </v-container>
                  <v-container
                    class="new-practice-card-container py-0"
                    v-if="!practiceUserIsPracticeAdmin"
                  >
                    <v-select
                      v-if="isPracticeAdmin"
                      v-model="programs"
                      :items="availablePrograms"
                      :item-text="'name'"
                      return-object
                      chips
                      multiple
                      @input="updatePrograms($event)"
                      label="Programs"
                      placeholder="Select program"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip v-if="index === 0">
                          <span>{{ item.name }}</span>
                        </v-chip>
                        <span
                          v-if="index === 1"
                          class="grey--text text-caption"
                        >
                          (+{{ programs.length - 1 }} others)
                        </span>
                      </template>
                    </v-select>
                    <div v-else>
                      <label class="dialog-input-label">Programs</label>
                      <v-chip-group
                        v-if="
                          practiceUser.programs && practiceUser.programs.length
                        "
                      >
                        <v-chip
                          :disabled="!isAnyAdminRole"
                          v-for="program in practiceUser.programs"
                          :key="`user-program-${program.id}`"
                          >{{ program.name }}</v-chip
                        >
                      </v-chip-group>
                      <p v-else>No programs have been assigned.</p>
                    </div>
                  </v-container>
                  <v-container
                    v-if="isAnyAdminRole"
                    class="new-practice-card-footer d-flex justify-space-between"
                  >
                    <v-btn
                      rounded
                      color="primary"
                      elevation="0"
                      :disabled="!isPracticeActive"
                      @click="$emit('openNewUserEmailDialog')"
                    >
                      Send welcome email
                    </v-btn>
                  </v-container>
                </v-tab-item>
              </v-tabs-items>
              <v-container
                class="new-practice-card-footer pt-0 d-flex justify-space-between"
              >
                <v-btn
                  v-if="isPermitted && !practiceUser.isActive"
                  text
                  color="bhRedDark"
                  class="rounded-pill mr-2"
                  elevation="0"
                  @click="$emit('openDeleteUserDialog')"
                >
                  Delete user
                </v-btn>
                <v-btn
                  v-if="isPermitted"
                  :disabled="!isPracticeActive"
                  rounded
                  text
                  color="bhRedDark"
                  elevation="0"
                  @click="$emit('openDeactivationDialog')"
                >
                  {{ practiceUser.isActive ? 'Inactivate' : 'Activate' }} user
                </v-btn>
                <v-btn
                  v-if="isAnyAdminRole"
                  :disabled="!formIsValid || !isPracticeActive"
                  rounded
                  color="primary"
                  type="submit"
                  elevation="0"
                >
                  Save changes
                </v-btn>
              </v-container>
            </v-tab-item>
            <v-tab-item v-if="isAnyAdminRole">
              <div class="new-practice-user-patients">
                <main-user-table
                  :headers="headers"
                  :items="practiceUserPatients"
                  :selectItems="practiceUserPatients"
                  :items-per-page="queryParams.pageSize"
                  :server-items-length="
                    practiceUserPatientsPagination.totalCount || -1
                  "
                  :page="queryParams.page"
                  :row-component="'practice-user-patients-table-row'"
                  @to-user="toPatient"
                  @action:get-data="
                    data =>
                      practiceUser &&
                      practiceUser.id &&
                      getDataAction(
                        { ...data, practiceUserId: practiceUser.id },
                        false,
                      )
                  "
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import DialogInput from '@/components/forms/DialogInput.vue'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import MainUserTable from '@/components/tables/MainUserTable'
import RouterMixin from '@/mixins/router'
import { debounce } from '@/helpers/debounce'
import TenantTextsMixin from '@/mixins/tenantTexts'
import BhSelect from '@/components/inputs/BhSelect.vue'
import { AI_LENS_OPTIONS, DEFAULT_LANGUAGE } from '../../helpers/constants'

export default {
  name: 'PracticeUserDialog',
  components: {
    DialogInput,
    MainUserTable,
    DialogSelect,
    BhSelect,
  },
  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    practiceId: {
      type: Number,
      default: 0,
    },
  },
  mixins: [RouterMixin, TenantTextsMixin],
  data() {
    return {
      AI_LENS_OPTIONS,
      dialog: false,
      editedUser: { practiceId: this.practiceId },
      userToSave: false,
      tab: null,
      generalTab: null,
      headers: [],
      patients: [],
      rules: {
        text: [v => !!v || 'Value cannot by empty'], // @todo
      },
      programs: [],
    }
  },
  watch: {
    showDialog: function(val) {
      if (val) {
        this.dialog = true

        if (this.practiceUser) {
          this.getDataAction(
            {
              ...this.queryParams,
              sortBy: null,
              practiceUserId: this.practiceUser.id,
            },
            false,
          )
        }
      }
      this.dialog = val

      if (val === false) {
        this.closeDialog()
      }
    },
    practiceUser: function(val) {
      if (val.id) {
        this.editedUser = {
          firstName: val.firstName,
          lastName: val.lastName,
          phone: val.phone,
          roleId: val.roleId,
          aiLens: val.aiLens,
        }
      }
      if (val.id && this.dialog) {
        this.getDataAction(
          { ...this.queryParams, sortBy: null, practiceUserId: val.id },
          false,
        )
      }

      this.programs = val?.programs || []

      if (this.isPracticeAdmin) {
        this.getPrograms({
          companyId: this.practiceId,
          active: true,
        })
      }
    },
  },
  computed: {
    ...mapState('practicesStates', [
      'practice',
      'practiceUser',
      'practiceUserPatients',
      'practiceUserPatientsPagination',
    ]),
    ...mapState('programsStates', {
      availablePrograms: 'programs',
      availableProgramsLoading: 'loading',
    }),
    ...mapState('dictionariesStates', [
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('userStates', ['profileId']),
    isPracticeAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    isPracticeAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    practiceUserIsPracticeAdmin() {
      return (
        this.practiceUser.roleId ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    isPermitted() {
      if (
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator &&
        this.practiceUser.profileId != this.profileId
      )
        return true
      else if (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Admin
      )
        return true
      else return false
    },
    isAdvisorRoleSelected() {
      return this.editedUser.roleId === this.dictionaryPracticeUserRole.Advisor
    },
    isEmailChangeAllowed() {
      return this.dictionaryBhUsersRoleIds[this.getRolename] ? true : false
    },
    isAdvisor() {
      return this.dictionaryPracticeUserRole.Advisor == this.practiceUser.roleId
    },
    formIsValid() {
      return this.userToSave
    },
    roleIdOptions() {
      return [
        {
          text: 'Administrator',
          value: this.dictionaryPracticeUserRole.Administrator,
        },
        {
          text: 'Advisor',
          value: this.dictionaryPracticeUserRole.Advisor,
        },
        {
          text: 'Team',
          value: this.dictionaryPracticeUserRole.Team,
        },
      ]
    },
    isAnyAdminRole() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    computedFeatureFlagsSurvey() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    isPracticeActive() {
      return this.practice.isActive
    },
  },
  methods: {
    ...mapActions('programsStates', [
      'getPrograms',
      'assignProfilesToProgram',
      'unassignProfilesFromProgram',
      'assignProgramsToProfile',
    ]),
    ...mapActions('practicesStates', [
      'getPracticeUserPatients',
      'changePracticeUserFeatureFlag',
    ]),
    async updatePrograms(newPrograms) {
      if (this.practiceUser?.profileId) {
        const programs = [...newPrograms].map(el => ({ programId: el.id }))
        this.assignProgramsToProfile({
          profileId: this.practiceUser?.profileId,
          programs,
        })
      }
    },
    getData(data = {}) {
      if (!this.isAnyAdminRole) return
      if (this.dialog) {
        if (data && data.delete && data.get('sortBy')) {
          data.delete('sortBy')
        }
        debounce(
          () => {
            this.getPracticeUserPatients(data)
          },
          100,
          'getPracticeUserPatients',
        )
      }
    },
    closeDialog() {
      this.dialog = false
      this.editedUser = { practiceId: this.practiceId }
      this.userToSave = false
      this.tab = null
      this.generalTab = null
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    changeFeatureFlag(key, value) {
      this.onInput({ name: key, value })
      this.changePracticeUserFeatureFlag({
        key,
        value,
        practiceId:
          this.practiceId ||
          this.editedUser.practiceId ||
          this.userToSave.practiceId,
        userId:
          this.practiceUser.id || this.editedUser.id || this.userToSave.id,
      })
    },
    onInput({ name, value }) {
      this.editedUser[name] = value
      this.userToSave = { ...this.practiceUser, ...this.editedUser }
    },
    clearForm() {
      this.$refs.form.reset()
    },
    onSubmit() {
      this.$emit('on-submit', {
        ...this.userToSave,
        aiLens: this.isAdvisorRoleSelected ? this.userToSave?.aiLens : null,
        language: this.userToSave?.language || DEFAULT_LANGUAGE,
      })
      this.editedUser = { practiceId: this.practiceId }
      this.userToSave = false
      this.closeDialog()
    },
    toPatient(id) {
      this.$router.push({
        name: 'Patient',
        path: '/participants/:id',
        params: { id: id },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.new-practice {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
      p {
        color: $bh-gray--2;
      }
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-weight: bold;
  }
  &-tabs {
    padding: 0 24px;
    background: #ffffff;
    flex-grow: 0;
    .v-tab {
      color: $bh-blue;
      text-transform: none;
      letter-spacing: 0;
      font-size: 1em;
      &:not(.v-tab--active) {
        color: white;
      }
    }
    .v-tabs-slider {
      background: $bh-blue;
    }
  }
  &-v-tabs-items {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    background: $bh-blue--4 !important;
    .v-window__container {
      min-height: 100%;
      overflow-y: auto;
    }
    .v-window-item {
      display: flex;
      flex-grow: 1;
      flex-direction: column;
    }
  }

  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-form {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &-body {
      height: 100%;
      display: flex;
      flex-direction: column;
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
      .v-toolbar {
        flex-grow: 0;
      }

      .bh-select-label {
        font-weight: inherit;
        font-size: inherit;
        line-height: inherit;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
  &-user-patients {
    padding: 12px 24px;
  }
}
</style>
